import { Check, Lock, Palette, Eye, Film, Mic, Download, Lightbulb, Printer, SkipForward } from "lucide-react";

const stages = [
  { id: "strategy", label: "Strategy", icon: Lightbulb, description: "Choose workflow" },
  { id: "compose", label: "Compose", icon: Palette, description: "Set up your shot" },
  { id: "preview", label: "Preview", icon: Eye, description: "Review composition" },
  { id: "animate", label: "Animation Review", icon: Film, description: "Review your animation" },
  { id: "lipsync", label: "Lip Sync ADR", icon: Mic, description: "Add voice sync" },
  { id: "print", label: "Print", icon: Printer, description: "Save & Finalize" }
];

export function PipelineHeader({ 
  currentStage, 
  onStageChange, 
  stageStatus = {},
  skippedStages = [], // Stages to show as skipped/greyed out
  forceAccessibleStages = [], // Stages that should always be clickable
  disabled = false,
  generating = false // Animation generation in progress
}) {
  const getStageState = (stageId) => {
    const stageIndex = stages.findIndex(s => s.id === stageId);
    const currentIndex = stages.findIndex(s => s.id === currentStage);
    
    // Check if stage is skipped (video uploaded workflow)
    if (skippedStages.includes(stageId)) {
      return "skipped";
    }
    
    // Current active stage - always show as active
    if (stageId === currentStage) {
      return "active";
    }
    
    // Check if stage is completed
    if (stageStatus[stageId]?.completed) {
      return "completed";
    }
    
    // Check if stage is force accessible (e.g., Print when lip sync exists)
    if (forceAccessibleStages.includes(stageId)) {
      return "available";
    }
    
    // Check if stage is available (previous stages completed)
    const canAccess = stages.slice(0, stageIndex).every(s => 
      s.id === "compose" || stageStatus[s.id]?.completed || skippedStages.includes(s.id)
    );
    
    if (canAccess) {
      return "available";
    }
    
    return "locked";
  };

  const handleStageClick = (stageId) => {
    if (disabled) return;
    const state = getStageState(stageId);
    
    // Always allow clicking completed or force-accessible stages, even when generating
    if (state === "completed" || forceAccessibleStages.includes(stageId)) {
      onStageChange(stageId);
      return;
    }
    
    // Allow clicking skipped stages (they will show toast) or non-locked stages
    if (state !== "locked") {
      onStageChange(stageId);
    }
  };

  return (
    <div className="bg-zinc-900/80 backdrop-blur-sm border-b border-zinc-800 px-4 py-3" data-testid="pipeline-header">
      <div className="flex items-center justify-center gap-2 md:gap-4">
        {stages.map((stage, index) => {
          const state = getStageState(stage.id);
          const Icon = stage.icon;
          const isLast = index === stages.length - 1;
          // Completed and force-accessible stages are always clickable (unless fully disabled)
          const isClickable = state === "completed" || forceAccessibleStages.includes(stage.id) || (state !== "locked" && state !== "skipped");
          
          return (
            <div key={stage.id} className="flex items-center">
              {/* Stage Button */}
              <button
                onClick={() => handleStageClick(stage.id)}
                disabled={disabled || (!isClickable && state === "locked")}
                data-testid={`stage-${stage.id}`}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 relative
                  ${state === "active" 
                    ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/40 ring-2 ring-indigo-400 ring-offset-2 ring-offset-zinc-900" 
                    : state === "completed"
                      ? "bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 cursor-pointer"
                      : state === "skipped"
                        ? "bg-zinc-800/30 text-zinc-600 line-through decoration-zinc-600 cursor-not-allowed opacity-50"
                        : state === "available"
                          ? "bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white cursor-pointer"
                          : "bg-zinc-800/50 text-zinc-600 cursor-not-allowed"
                  }
                `}
              >
                {/* Active indicator pulse */}
                {state === "active" && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500"></span>
                  </span>
                )}
                <div className={`
                  w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                  ${state === "active" 
                    ? "bg-white/20" 
                    : state === "completed"
                      ? "bg-emerald-500"
                      : state === "skipped"
                        ? "bg-zinc-700/30"
                        : state === "available"
                          ? "bg-zinc-700"
                          : "bg-zinc-700/50"
                  }
                `}>
                  {state === "completed" ? (
                    <Check className="w-3 h-3" />
                  ) : state === "skipped" ? (
                    <SkipForward className="w-3 h-3" />
                  ) : state === "locked" ? (
                    <Lock className="w-3 h-3" />
                  ) : (
                    <Icon className="w-3 h-3" />
                  )}
                </div>
                <div className="hidden sm:block text-left">
                  <div className={`text-sm font-medium ${state === "skipped" ? "line-through" : ""}`}>{stage.label}</div>
                  <div className={`text-[10px] ${state === "active" ? "text-indigo-200" : state === "skipped" ? "text-zinc-600" : "text-zinc-500"}`}>
                    {state === "skipped" ? "Skipped" : stage.description}
                  </div>
                </div>
                <span className={`sm:hidden text-xs font-medium ${state === "skipped" ? "line-through" : ""}`}>{stage.label}</span>
              </button>
              
              {/* Connector Line */}
              {!isLast && (
                <div className={`
                  w-8 md:w-12 h-0.5 mx-1
                  ${state === "completed" || state === "skipped" || stages.findIndex(s => s.id === currentStage) > index
                    ? state === "skipped" ? "bg-zinc-700/50" : "bg-indigo-500"
                    : "bg-zinc-700"
                  }
                `} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PipelineHeader;
